import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Minus, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../../hooks/useTranslation';

const MaterialQuoteModal = ({ isOpen, onClose, material }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    quantity: 1,
    deliveryDate: '',
    deliveryAddress: '',
    specialRequirements: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleQuantityChange = (change) => {
    setFormData(prev => ({
      ...prev,
      quantity: Math.max(1, prev.quantity + change)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(t('modals.materialQuote.successMessage'), {
        description: `${t('modals.materialQuote.successMessage')} ${material?.name}. ${t('modals.quote.successDescription')}`,
        duration: 5000,
      });
      onClose();
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        quantity: 1,
        deliveryDate: '',
        deliveryAddress: '',
        specialRequirements: ''
      });
    }, 1500);
  };

  if (!material) return null;

  const estimatedTotal = material.price.includes('€') 
    ? parseFloat(material.price.replace('€', '').replace(',', '')) * formData.quantity
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-medium text-gray-900">
            {t('modals.materialQuote.requestQuote')}: {material.name}
          </DialogTitle>
          <div className="flex items-center space-x-4 mt-2">
            <Badge variant="outline">{material.category}</Badge>
            <Badge variant="outline">{material.country}</Badge>
            <Badge className={material.inStock ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
              {material.inStock ? t('materials.inStock') : t('materials.outOfStock')}
            </Badge>
          </div>
        </DialogHeader>
        
        {/* Material Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-4">
            <img 
              src={material.image} 
              alt={material.name}
              className="w-20 h-20 object-cover rounded-lg"
            />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{material.name}</h4>
              <p className="text-gray-600 text-sm mb-2">{material.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-semibold text-gray-900">{material.price}</span>
                <Badge variant="outline">{material.certification}</Badge>
              </div>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.fullName')} *
              </label>
              <Input
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('common.placeholder.enterFullName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.emailAddress')} *
              </label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('common.placeholder.enterEmail')}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.companyName')}
              </label>
              <Input
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder={t('common.placeholder.enterCompany')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('contact.phoneNumber')}
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder={t('common.placeholder.enterPhone')}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modals.materialQuote.quantityRequired')} *
              </label>
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={formData.quantity <= 1}
                >
                  <Minus size={16} />
                </Button>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 1)}
                  className="w-24 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(1)}
                >
                  <Plus size={16} />
                </Button>
                <span className="text-sm text-gray-600">{t('modals.materialQuote.units')}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('modals.materialQuote.requiredDeliveryDate')}
              </label>
              <Input
                type="date"
                value={formData.deliveryDate}
                onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('modals.materialQuote.deliveryAddress')} *
            </label>
            <Textarea
              required
              rows={2}
              value={formData.deliveryAddress}
              onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
              placeholder={t('common.placeholder.enterAddress')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('modals.materialQuote.specialRequirements')}
            </label>
            <Textarea
              rows={3}
              value={formData.specialRequirements}
              onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
              placeholder={t('common.placeholder.enterRequirements')}
            />
          </div>

          {/* Estimated Total */}
          {estimatedTotal > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('modals.materialQuote.estimatedTotal')} ({formData.quantity} {t('modals.materialQuote.units')}):</span>
                <span className="text-2xl font-semibold text-blue-900">
                  €{estimatedTotal.toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                {t('modals.materialQuote.finalPriceNote')}
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('modals.quote.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !material.inStock}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('modals.quote.sending')}
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  {material.inStock ? t('materials.requestQuote') : t('materials.notifyWhenAvailable')}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialQuoteModal;